import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Axios from "axios";
import { getAuthHeader } from "../../../utils/authToken";

const ManageCustomExamsModal = ({ open, onClose, courseId }) => {
    const [customExams, setCustomExams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        if (open) {
            fetchCustomExams();
            setSearchQuery("");
            setSearchResults([]);
            setError("");
            setSuccess("");
        }
    }, [open]);

    const fetchCustomExams = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/custom-exams`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setCustomExams(response.data);
        } catch (err) {
            console.error("Fetch custom exams error:", err);
            setError("Failed to load custom exams");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;

        setSearching(true);
        setError("");
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/ett-exams/search`,
                {
                    params: { q: searchQuery.trim() },
                    headers: { Authorization: getAuthHeader() },
                }
            );
            setSearchResults(response.data);
        } catch (err) {
            console.error("Search exams error:", err);
            setError("Failed to search ETT exams");
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const handleAddExam = async (exam) => {
        setError("");
        setSuccess("");
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/custom-exams`,
                { ett_exam_id: exam.id, exam_title: exam.title },
                { headers: { Authorization: getAuthHeader() } }
            );
            await fetchCustomExams();
            setSearchResults(prev => prev.filter(e => e.id !== exam.id));
            setSuccess(`"${exam.title}" added successfully`);
        } catch (err) {
            if (err.response?.status === 409) {
                setError("This exam is already added");
            } else {
                console.error("Add exam error:", err);
                setError("Failed to add exam");
            }
        }
    };

    const handleDeleteExam = async (examId) => {
        setError("");
        setSuccess("");
        try {
            const exam = customExams.find(e => e.id === examId);
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/custom-exams/${examId}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setCustomExams(prev => prev.filter(e => e.id !== examId));
            setSuccess(`"${exam?.exam_title || 'Exam'}" removed successfully`);
        } catch (err) {
            console.error("Delete exam error:", err);
            setError("Failed to remove exam");
        }
    };

    const customExamIds = new Set(customExams.map(e => e.ett_exam_id));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Manage Custom Exams</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Only A Series SAT exams are visible by default. If you want teachers to see other exams from ETT, add the exams below.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {/* Current custom exams */}
                <Typography variant="subtitle2" gutterBottom>
                    Current Custom Exams
                </Typography>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                    </Box>
                ) : customExams.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No custom exams added yet.
                    </Typography>
                ) : (
                    <List dense sx={{ mb: 2 }}>
                        {customExams.map((exam) => (
                            <ListItem key={exam.id}>
                                <ListItemText primary={exam.exam_title} />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => handleDeleteExam(exam.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Search and add */}
                <Typography variant="subtitle2" gutterBottom>
                    Add Exam from ETT
                </Typography>
                <Box display="flex" gap="10px" mb="10px">
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search ETT exams..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
                        onClick={handleSearch}
                        disabled={searching || !searchQuery.trim()}
                    >
                        Search
                    </Button>
                </Box>

                {searchResults.length > 0 && (
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {searchResults.map((exam) => {
                            const alreadyAdded = customExamIds.has(exam.id);
                            return (
                                <ListItem key={exam.id}>
                                    <ListItemText
                                        primary={exam.title}
                                        secondary={exam.type_label}
                                        sx={{ opacity: alreadyAdded ? 0.5 : 1 }}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() => handleAddExam(exam)}
                                            disabled={alreadyAdded}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageCustomExamsModal;
